import React, { useState, useEffect, useRef } from "react";
import Draggable from "react-draggable";
import { Input } from "reactstrap";
import { StyledTooltip } from "../../../views/project/label/annotation/EditorView/EditorTopNavigationBar/EditorTopNavigationBar";
import useImageZoom from "./useImageZoom";
import { loadRenderableImageSource } from "./dicomImage";

// ---- shape detection helpers ----
const isBBoxRow = (r) =>
  Array.isArray(r) &&
  r.length >= 6 &&
  Number.isFinite(Number(r[0])) &&
  Number.isFinite(Number(r[1])) &&
  Number.isFinite(Number(r[2])) &&
  Number.isFinite(Number(r[3])) &&
  typeof r[4] === "string"; // label

const isBBoxArray = (arr) => Array.isArray(arr) && arr.length > 0 && isBBoxRow(arr[0]);

// Some older payloads had Groundtruth nested like: [ [ [x,y,w,h,label,color], ... ] ]
const isOldNestedBBoxArray = (arr) =>
  Array.isArray(arr) && Array.isArray(arr[0]) && !isBBoxRow(arr) && isBBoxRow(arr[0][0]);

// Polygons your code expects: [[ [x,y], [x,y], ..., [label,color] ], ...]
const looksLikePolygon = (arr) =>
  Array.isArray(arr) &&
  Array.isArray(arr[0]) &&
  Array.isArray(arr[0][0]) &&
  typeof arr[0].slice(-1)[0]?.[0] === "string";

const isPolygonRow = (row) =>
  Array.isArray(row) &&
  Array.isArray(row[0]) &&
  typeof row.slice(-1)[0]?.[0] === "string";

const normalizePolygonRows = (value) => {
  if (!Array.isArray(value)) return [];
  if (isPolygonRow(value)) return [value];
  if (value.every(isPolygonRow)) return value;

  return value.flatMap((item) => normalizePolygonRows(item));
};

export default function Canvas(props) {
  const [currentEpoch, setCurrentEpoch] = useState();
  const [canGrabWipe, setCanGrabWipe] = useState(false);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });

  // console.log(props);

  const onGestureHover = (e) => {
    const imgX = getImageXFromPointer(e);
    if (imgX == null) {
      setCanGrabWipe(false);
      return;
    }

    const imgW = canvasRef.current.width;
    const handleX = imgW * reveal;
    const HIT_SLOP = imgW * 0.02;

    setCanGrabWipe(Math.abs(imgX - handleX) < HIT_SLOP);
  };

  useEffect(() => {
    if (props.controlledEpoch !== undefined) {
      setCurrentEpoch(props.controlledEpoch);
      return;
    }

    if (props.epochLength !== undefined) {
      setCurrentEpoch(props.epochLength);
    }
  }, [props.epochLength, props.controlledEpoch]);

  const canvasRef = useRef(null);
  const wrapperRef = useRef(null);
  const zoomControlsRef = useRef(null);
  const dicomSliderRef = useRef(null);
  const gestureRef = useRef(null);
  const [dicomDraftSlice, setDicomDraftSlice] = useState(
    props.dicomSliceControl?.value || 1
  );
  const dicomControlEnabled = props.dicomSliceControl?.enabled;
  const dicomControlLoading = props.dicomSliceControl?.loading;
  const dicomControlValue = props.dicomSliceControl?.value;
  const onDicomSliceChange = props.dicomSliceControl?.onChange;
  const {
    transform,
    transformStyle,
    zoomIn,
    zoomOut,
    resetZoom,
    viewportHandlers,
  } = useImageZoom({ viewportRef: wrapperRef });

  // A newly selected image should always start fully fitted in the viewport,
  // regardless of the zoom or pan used on the previous image.
  useEffect(() => {
    resetZoom();
  }, [props.src, resetZoom]);

  useEffect(() => {
    if (dicomControlEnabled) {
      setDicomDraftSlice(dicomControlValue || 1);
    }
  }, [dicomControlEnabled, dicomControlValue]);

  useEffect(() => {
    if (
      !dicomControlEnabled ||
      dicomControlLoading ||
      dicomDraftSlice === dicomControlValue
    ) {
      return undefined;
    }

    const timeout = window.setTimeout(() => {
      onDicomSliceChange?.(dicomDraftSlice);
    }, 90);

    return () => window.clearTimeout(timeout);
  }, [
    dicomDraftSlice,
    dicomControlEnabled,
    dicomControlLoading,
    dicomControlValue,
    onDicomSliceChange,
  ]);

  const [reveal, setReveal] = useState(0.5);     // 0..1
  const [draggingWipe, setDraggingWipe] = useState(false);

  // cache overlay images
  const maskImgRef = useRef(new Map());
  const depthImgRef = useRef(null);

  const clamp01 = (n) => Math.max(0, Math.min(1, n));
  const getOverlayOpacity = (value, fallback = 1) => {
    if (value === null || value === undefined || value === "") return fallback;

    const opacity = Number(value);
    return Number.isFinite(opacity) ? clamp01(opacity) : fallback;
  };

  /** When a sidebar row is hovered, keep that annotation vivid and fade the rest. */
  const getHoverAwareOpacity = (baseOpacity, { source, index, label } = {}) => {
    const hover = props.hoveredAnnotation;
    const resolvedBase = getOverlayOpacity(baseOpacity, 0.4);

    if (!hover?.source) return resolvedBase;

    const labelMatch =
      hover.label != null &&
      label != null &&
      String(hover.label).toLowerCase() === String(label).toLowerCase();
    const indexMatch =
      hover.index != null && index != null && Number(hover.index) === Number(index);

    // Prefer index when the sidebar row targets a specific annotation
    // (multiple boxes can share the same class label, e.g. "pothole").
    const isFocused =
      hover.source === source &&
      (hover.index != null ? indexMatch : Boolean(labelMatch));

    if (isFocused) return Math.min(1, Math.max(resolvedBase, 0.72));
    return resolvedBase * 0.18;
  };

  const getImageXFromPointer = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;

    const rect = canvas.getBoundingClientRect();
    const cssX = e.clientX - rect.left;

    const scaleX = (imageSize.width || canvas.width) / rect.width;
    return cssX * scaleX;
  };

  const getX01FromEvent = (e) => {
    const el = gestureRef.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    const x = e.clientX - rect.left;
    return clamp01(x / rect.width);
  };

  const onWipePointerDown = (e) => {
    const hasOverlay =
      props?.each_image_detail?.mask_url ||
      props?.each_image_detail?.depth_map_url;
    if (!hasOverlay) return;

    const canvas = canvasRef.current;
    const el = gestureRef.current;
    if (!canvas || !el) return;

    const imgX = getImageXFromPointer(e);
    if (imgX == null) return;

    const imgW = imageSize.width || canvas.width;
    const handleX = imgW * reveal;
    const HIT_SLOP = imgW * 0.02; // 2% of image width

    // only grab slider handle
    if (Math.abs(imgX - handleX) > HIT_SLOP) return;

    e.preventDefault();
    e.stopPropagation();

    setDraggingWipe(true);
    el.setPointerCapture?.(e.pointerId);

    setReveal(Math.max(0, Math.min(1, imgX / imgW)));
  };


  const onWipePointerMove = (e) => {
    if (!draggingWipe) return;

    e.preventDefault();
    e.stopPropagation();

    const imgX = getImageXFromPointer(e);
    if (imgX == null) return;

    const imgW = canvasRef.current.width;
    setReveal(Math.max(0, Math.min(1, imgX / imgW)));
  };


  const stopWipe = (e) => {
    if (!draggingWipe) return;

    e.preventDefault();
    e.stopPropagation();

    setDraggingWipe(false);
    gestureRef.current?.releasePointerCapture?.(e.pointerId);
  };
  const getRoundedScore = (value) => Number(Number(value ?? 0).toFixed(2));

  const getSegmentationMasksToDraw = () => {
    const detail = props?.each_image_detail ?? {};
    const maskUrlData = detail?.mask_url;

    if (!maskUrlData) return [];

    // old single base64 support
    if (typeof maskUrlData === "string") {
      return [{ label: "mask", url: maskUrlData, score: 1 }];
    }

    const annotationGroup = Array.isArray(props?.annotations)
      ? props.annotations?.[0]
      : [];

    const threshold = Number(props?.scoreThreshold ?? 0);

    return Object.entries(maskUrlData)
      .map(([label, url]) => {
        const matchedAnnotation = annotationGroup?.find((ann) => ann?.[0] === label);
        const score = getRoundedScore(matchedAnnotation?.[2] ?? 0);

        return { label, url, score };
      })
      .filter(({ score }) => score >= threshold);
  };

  const loadImage = (src) =>
    new Promise((resolve) => {
      const image = new Image();
      image.crossOrigin = "anonymous";

      image.onload = () => resolve(image);
      image.onerror = () => resolve(null);

      image.src =
        src?.startsWith("http") || src?.startsWith("data:")
          ? src
          : `data:image/png;base64,${src}`;
    });

  const preloadSegmentationMasks = async (masksToDraw) => {
    const loadPromises = masksToDraw.map(async ({ label, url, score }) => {
      const cacheKey = url || label;

      let cachedImg = maskImgRef.current.get(cacheKey);
      if (!cachedImg) {
        cachedImg = await loadImage(url);
        if (cachedImg) {
          maskImgRef.current.set(cacheKey, cachedImg);
        }
      }

      return {
        label,
        score,
        img: cachedImg,
      };
    });

    return Promise.all(loadPromises);
  };

  const drawSegmentationMasks = async (ctx, img) => {
    const masksToDraw = getSegmentationMasksToDraw();
    if (!masksToDraw.length) return;

    const loadedMasks = await preloadSegmentationMasks(masksToDraw);

    ctx.save();
    ctx.globalAlpha = getOverlayOpacity(props.opacity, 1);

    loadedMasks.forEach(({ img: maskImg }) => {
      if (!maskImg) return;
      ctx.drawImage(maskImg, 0, 0, img.width, img.height);
    });

    ctx.restore();
  };

  useEffect(() => {
    const visibleCtx = canvasRef.current?.getContext?.("2d");
    if (!visibleCtx) return undefined;

    let ctx = visibleCtx;
    let cancelled = false;
    let logicalCanvasWidth = visibleCtx.canvas.width;
    let logicalCanvasHeight = visibleCtx.canvas.height;
    let activeImage = null;

    const hasSegmentationMaskOverlay =
      Boolean(props?.pridictions && props?.each_image_detail?.mask_url) ||
      Boolean(
        props?.groundtruth &&
        props?.each_image_detail?.task_type === "segmentation" &&
        props?.each_image_detail?.ground_truth?.[0]
      );

    const commitCanvas = (sourceCanvas) => {
      if (cancelled || sourceCanvas === visibleCtx.canvas) return;

      visibleCtx.setTransform(1, 0, 0, 1, 0, 0);
      visibleCtx.save();
      visibleCtx.globalAlpha = 1;
      visibleCtx.clearRect(0, 0, visibleCtx.canvas.width, visibleCtx.canvas.height);
      visibleCtx.drawImage(sourceCanvas, 0, 0, visibleCtx.canvas.width, visibleCtx.canvas.height);
      visibleCtx.restore();
    };

    // helper scoped to this effect for text sizing with image/canvas dims
    function getFontForCanvas(cw) {
      const size = cw * 0.02; // used in bbox labels
      return "bold " + (size | 0) + "px Montserrat";
    }

    function getScaledFontSize(canvasW, canvasH, labelText = "") {
      const width = Math.max(1, Number(canvasW) || 0);
      const height = Math.max(1, Number(canvasH) || 0);
      const base = Math.min(width, height);
      const responsiveFloor = Math.min(18, Math.max(6, base * 0.08));
      let size = Math.min(Math.max(base * 0.03, responsiveFloor), 140);

      const textLength = String(labelText).trim().length;
      if (textLength > 0) {
        // Approximate Montserrat's text width plus horizontal label padding.
        // Keep the complete badge below 70% of the source image width.
        const estimatedBadgeWidth = (textLength * 0.62 + 0.8) * size;
        const maxBadgeWidth = width * 0.7;
        if (estimatedBadgeWidth > maxBadgeWidth) {
          size *= maxBadgeWidth / estimatedBadgeWidth;
        }
      }

      return Math.max(4, size);
    }

    function getScaledFont(canvasW, canvasH, weight = "bold", labelText = "") {
      const size = getScaledFontSize(canvasW, canvasH, labelText);
      return `${weight} ${Math.round(size)}px Montserrat`;
    }

    function getBoundingBoxLabelFontSize(
      canvasW,
      canvasH,
      boxW,
      boxH,
      labelText = ""
    ) {
      const baseSize = getScaledFontSize(canvasW, canvasH, labelText);
      const width = Math.abs(Number(boxW) || 0);
      const height = Math.abs(Number(boxH) || 0);

      if (!width || !height) return baseSize;

      const minimumSize = Math.min(
        10,
        Math.max(6, Math.min(canvasW, canvasH) * 0.01)
      );
      const textUnits = Math.max(1, String(labelText).trim().length * 0.62 + 0.8);
      const widthLimitedSize = width / textUnits;
      const heightLimitedSize = height * 0.35;

      return Math.max(
        minimumSize,
        Math.min(baseSize, widthLimitedSize, heightLimitedSize)
      );
    }

    const normalizedTaskType = String(
      props?.each_image_detail?.task_type || props?.taskTypes || ""
    ).toLowerCase().replace(/[\s-]+/g, "_");
    const normalizedModelName = String(
      props?.modelName ||
      props?.each_image_detail?.model_name ||
      props?.each_image_detail?.model ||
      ""
    ).toLowerCase().replace(/[\s_]+/g, "");
    const isSuperpointKeypointDetection =
      normalizedModelName === "superpoint" &&
      normalizedTaskType === "keypoint_detection";

    const collectSuperpointLandmarks = (value) => {
      if (!Array.isArray(value)) return [];
      if (
        value.length >= 3 &&
        [value[0], value[1], value[2]].every((item) =>
          Number.isFinite(Number(item))
        )
      ) {
        return [value];
      }

      return value.flatMap(collectSuperpointLandmarks);
    };

    const drawSuperpointLandmarks = () => {
      if (!isSuperpointKeypointDetection || !props?.pridictions) return;

      const canvasW = logicalCanvasWidth;
      const canvasH = logicalCanvasHeight;
      const dotRadius = Math.max(2, Math.min(canvasW, canvasH) * 0.005);
      const labelFont = Math.max(12, Math.min(canvasW, canvasH) * 0.018);
      const threshold = Number(props?.scoreThreshold ?? 0);

      collectSuperpointLandmarks(props?.landmarks).forEach(
        ([lx, ly, pointScore], pointIndex) => {
          if (Number(pointScore) < threshold) return;

          ctx.save();
          ctx.globalAlpha = Number.isFinite(props?.opacity) ? props.opacity : 0.7;
          ctx.fillStyle = "red";
          ctx.beginPath();
          ctx.arc(Number(lx), Number(ly), dotRadius, 0, 2 * Math.PI);
          ctx.fill();

          if (props?.labelvisible) {
            ctx.globalAlpha = 1;
            ctx.font = `bold ${labelFont}px sans-serif`;
            ctx.textAlign = "left";
            ctx.textBaseline = "bottom";
            ctx.lineWidth = Math.max(2, labelFont * 0.2);
            ctx.strokeStyle = "rgba(0, 0, 0, 0.8)";
            ctx.fillStyle = "#fff";
            const labelX = Number(lx) + dotRadius + 2;
            const labelY = Number(ly) - dotRadius;
            ctx.strokeText(String(pointIndex + 1), labelX, labelY);
            ctx.fillText(String(pointIndex + 1), labelX, labelY);
          }
          ctx.restore();
        }
      );
    };


    // drawing helpers
    function drawPolygonWithFill(labelText, anchors, labelColor, x, y, opacity, fillcolors, pattern) {
      ctx.save();
      ctx.fillStyle = fillcolors ?? labelColor;
      // dashed outline (bugfix: call the function)
      if (Array.isArray(pattern) && pattern.length) {
        try {
          ctx.setLineDash(pattern);
        } catch (_) { }
      } else {
        try {
          ctx.setLineDash([]); // solid when landmarks or no pattern
        } catch (_) { }
      }

      ctx.lineWidth = 4;
      ctx.strokeStyle = fillcolors ?? labelColor;
      ctx.beginPath();

      const first = anchors?.[0];
      if (!first) {
        ctx.restore();
        return;
      }
      ctx.moveTo(first.x ? first.x : first[0], first.y ? first.y : first[1]);

      for (let i = 1; i < anchors?.length; i++) {
        ctx.lineTo(anchors[i][0], anchors[i][1]);
      }
      ctx.closePath();

      ctx.globalAlpha = 1;
      ctx.stroke();
      ctx.globalAlpha = Number.isFinite(opacity) ? opacity : 0.4;
      ctx.fill();

      // label
      // ctx.font = "14px sans-serif";
      ctx.font = getScaledFont(logicalCanvasWidth, logicalCanvasHeight, "bold", labelText);

      ctx.fillStyle = fillcolors ?? labelColor;
      ctx.globalAlpha = 1;

      const finitePoints = anchors
        .map((pt) => [Number(pt?.[0]), Number(pt?.[1])])
        .filter(([px, py]) => Number.isFinite(px) && Number.isFinite(py));

      const getQuantile = (values, quantile = 0) => {
        if (!values.length) return 0;

        const sorted = [...values].sort((a, b) => a - b);
        const index = Math.max(
          0,
          Math.min(sorted.length - 1, Math.floor((sorted.length - 1) * quantile))
        );

        return sorted[index];
      };

      const yCoords = finitePoints.map(([, py]) => py);
      const robustMinY = getQuantile(yCoords, 0.02);
      const topEdgeTolerance = Math.max(4, logicalCanvasHeight * 0.01);
      const topEdgeXCoords = finitePoints
        .filter(([, py]) => py <= robustMinY + topEdgeTolerance)
        .map(([px]) => px);
      // Keep X and Y tied to the same part of the contour. Combining the
      // polygon's independent leftmost X and topmost Y can place the label
      // in empty space, which is especially visible on person silhouettes.
      const robustTopEdgeX = getQuantile(topEdgeXCoords, 0.5);

      if (labelText && labelText.trim() !== "") {
        const displayText =
          String(labelText);

        const metrics = ctx.measureText(displayText);
        const fontHeight =
          (metrics.fontBoundingBoxAscent || 0) +
          (metrics.fontBoundingBoxDescent || 0) ||
          getScaledFontSize(logicalCanvasWidth, logicalCanvasHeight, displayText);

        const paddingX = fontHeight * 0.4;
        const paddingY = fontHeight * 0.25;
        const rectWidth = metrics.width + paddingX * 2;
        const rectHeight = fontHeight + paddingY * 2;

        const bgColor = fillcolors ?? labelColor ?? "#ff3838";

        let labelX = robustTopEdgeX + 8;
        let labelY = robustMinY - rectHeight;

        if (labelY < 0) {
          labelY = robustMinY + 4;
        }

        if (labelX + rectWidth > logicalCanvasWidth) {
          labelX = logicalCanvasWidth - rectWidth - 5;
        }

        if (labelX < 5) {
          labelX = 5;
        }

        ctx.globalAlpha = 1;
        ctx.fillStyle = bgColor;
        ctx.fillRect(labelX, labelY, rectWidth, rectHeight);

        ctx.fillStyle = "#fff";
        ctx.textBaseline = "middle";
        ctx.fillText(
          displayText,
          labelX + paddingX,
          labelY + rectHeight / 2
        );
      }
      ctx.restore();
    }

    function getReadableFont(canvasW) {
      // Scale font based on canvas size but never below 16px
      const fontSize = Math.max(canvasW * 0.03, 16);
      return `bold ${fontSize}px Montserrat`;
    }


    function drawDashedLine(
      x,
      y,
      w,
      h,
      text,
      color,
      pattern,
      line,
      sc,
      al,
      opacity,
      track_id,
      fillcolors,
      landmarks
    ) {
      const canvasW = logicalCanvasWidth;
      const canvasH = logicalCanvasHeight;

      ctx.beginPath();
      ctx.globalAlpha = 1;
      // ctx.lineWidth = canvasW * 0.003;
      ctx.lineWidth = Math.max(2, canvasW * 0.003);


      if (Array.isArray(pattern) && pattern.length) {
        try {
          ctx.setLineDash(pattern);
        } catch (_) { }
      } else {
        try {
          ctx.setLineDash([]); // solid when landmarks or no pattern
        } catch (_) { }
      }

      const strokeFill = fillcolors ?? color ?? "#ff3838";
      ctx.strokeStyle = strokeFill;
      ctx.fillStyle = props.model_task_type !== "Face Landmark Detection" ? strokeFill : "transparent";

      const fillOpacity = Number.isFinite(opacity) ? opacity : 0.4;

      // stroke rect only if it has size
      ctx.globalAlpha = 1;
      if (w > 1 && h > 1) ctx.strokeRect(x, y, w, h);

      // semi-transparent fill for the box if no landmarks overlay
      if (!landmarks) {
        ctx.globalAlpha = fillOpacity;
        ctx.fillRect(x, y, w, h);
      }

      // label bg + text (clamped)
      // const smallFontSize = Math.min(Math.max(canvasW * 0.025, 14), 28);
      // ctx.font = "bold " + smallFontSize + "px Montserrat";


      const capitalizeFirst = (s = "") => {
        const str = String(s).trim();
        if (!str) return "";
        return str;
      };



      const className = capitalizeFirst(text);
      const hasTrackId =
        props.type === "dataset_versions" &&
        track_id !== null &&
        track_id !== undefined &&
        track_id !== "";
      const labelText = className
        ? `${className}${hasTrackId ? ` - ${track_id}` : ""}`
        : "";
      const labelFontSize = getBoundingBoxLabelFontSize(
        canvasW,
        logicalCanvasHeight,
        w,
        h,
        labelText
      );
      ctx.font = `bold ${Math.round(labelFontSize)}px Montserrat`;
      const labelMetrics = ctx.measureText(labelText);
      const labelWidth = labelMetrics.width;
      const margin = 5;

      if (labelText && labelText.trim() !== "") {
        const bgColor = strokeFill;
        ctx.globalAlpha = 1;
        ctx.fillStyle = bgColor;

        // if near top edge, draw inside the box; else draw above
        // const paddingX = 6;
        // const paddingY = 4;
        // const rectHeight = smallFontSize + paddingY * 2;
        // const rectWidth = textWidth + paddingX * 2;

        // derive font height safely from canvas
        const fontHeight =
          (labelMetrics.fontBoundingBoxAscent || 0) +
          (labelMetrics.fontBoundingBoxDescent || 0) ||
          labelFontSize;

        const paddingX = fontHeight * 0.4;
        const paddingY = fontHeight * 0.25;

        const rectHeight = fontHeight + paddingY * 2;
        const rectWidth = labelWidth + paddingX * 2;
        const labelX = Math.min(
          Math.max(Number(x) || 0, margin),
          Math.max(margin, canvasW - rectWidth - margin)
        );
        const drawInsideBox = y - rectHeight < margin;
        const labelY = drawInsideBox ? y : y - rectHeight;

        ctx.fillRect(labelX, labelY, rectWidth, rectHeight);
        ctx.fillStyle = "#fff";
        ctx.textAlign = "left";
        ctx.textBaseline = "middle";
        ctx.fillText(labelText, labelX + paddingX, labelY + rectHeight / 2);
      }
      //       if (labelText && labelText.trim() !== "") {
      //     // 1. Set dynamic font size based on image height
      //     const fontSize = Math.max(10, Math.floor(canvasH * 0.1)); 
      //     ctx.font = `${fontSize}px Arial`;

      //     const metrics = ctx.measureText(labelText);
      //     const textWidth = metrics.width;

      //     // 2. Calculate dimensions based on that font size
      //     const paddingX = fontSize * 0.5;
      //     const paddingY = fontSize * 0.2;
      //     const rectHeight = fontSize + (paddingY * 2);
      //     const rectWidth = textWidth + (paddingX * 2);

      //     ctx.globalAlpha = 1;
      //     ctx.fillStyle = strokeFill;

      //     // Boundary check: Draw inside if there is no room above
      //     if (y - rectHeight < 0) {
      //         ctx.fillRect(x, y, rectWidth, rectHeight);
      //         ctx.fillStyle = "#fff";
      //         ctx.textBaseline = "top"; 
      //         ctx.fillText(labelText, x + paddingX, y + paddingY);
      //     } else {
      //         ctx.fillRect(x, y - rectHeight, rectWidth, rectHeight);
      //         ctx.fillStyle = "#fff";
      //         ctx.textBaseline = "bottom";
      //         ctx.fillText(labelText, x + paddingX, y - paddingY);
      //     }
      // }

      // landmarks (small red dots)
      if (
        landmarks &&
        Array.isArray(landmarks) &&
        !isSuperpointKeypointDetection
      ) {
        const dotRadius = Math.max(2, Math.min(canvasW, canvasH) * 0.005);
        // ensures minimum size = 2px, scales with canvas

        landmarks.forEach(([lx, ly]) => {
          ctx.beginPath();
          ctx.globalAlpha = Number.isFinite(opacity) ? opacity : 0.7;
          ctx.fillStyle = "red";
          ctx.arc(lx, ly, dotRadius, 0, 2 * Math.PI);
          ctx.fill();
        });
      }
    }

    // main drawing routine
    if (props.imageName !== "image.svg") {
      const img = new Image();
      activeImage = img;
      img.decoding = "async";
      img.onload = async () => {
        if (cancelled) return;

        const renderCtx = hasSegmentationMaskOverlay
          ? document.createElement("canvas").getContext("2d")
          : visibleCtx;

        if (!renderCtx) return;
        ctx = renderCtx;

        // size canvas to image
        setImageSize({ width: img.width, height: img.height });
        props?.onImageSizeChange?.({ width: img.width, height: img.height });
        logicalCanvasWidth = img.width;
        logicalCanvasHeight = img.height;

        const zoomScale = Math.max(1, Number(transform?.scale) || 1);
        const pixelRatio = Math.min(2, window.devicePixelRatio || 1);
        const requestedRenderScale = zoomScale * pixelRatio;
        const maxBackingPixels = 16000000;
        const maxScaleForImage = Math.sqrt(
          maxBackingPixels / Math.max(1, img.width * img.height)
        );
        const renderScale = Math.max(
          1,
          Math.min(requestedRenderScale, maxScaleForImage)
        );
        const backingWidth = Math.max(1, Math.round(img.width * renderScale));
        const backingHeight = Math.max(1, Math.round(img.height * renderScale));

        visibleCtx.canvas.width = backingWidth;
        visibleCtx.canvas.height = backingHeight;
        visibleCtx.setTransform(renderScale, 0, 0, renderScale, 0, 0);
        visibleCtx.save();
        visibleCtx.globalAlpha = 1;
        visibleCtx.clearRect(0, 0, img.width, img.height);
        visibleCtx.drawImage(img, 0, 0, img.width, img.height);
        visibleCtx.restore();

        if (hasSegmentationMaskOverlay) {
          ctx.canvas.width = backingWidth;
          ctx.canvas.height = backingHeight;
          ctx.setTransform(renderScale, 0, 0, renderScale, 0, 0);
          ctx.globalAlpha = 1;
          ctx.clearRect(0, 0, img.width, img.height);
          ctx.drawImage(img, 0, 0, img.width, img.height);
        }

        const drawOverlayWipe = (overlayImg) => {
          if (!overlayImg) return;

          const imgW = img.width;
          const imgH = img.height;
          const wipeX = Math.round(imgW * reveal);

          // overlay only left side
          ctx.save();
          ctx.beginPath();
          ctx.rect(0, 0, wipeX, imgH);
          ctx.clip();
          ctx.globalAlpha = 1;
          ctx.drawImage(overlayImg, 0, 0, imgW, imgH);
          ctx.restore();

          // divider + handle
          ctx.save();
          const barW = Math.max(3, imgW * 0.004);
          ctx.fillStyle = "rgba(255,255,255,0.9)";
          ctx.fillRect(wipeX - barW / 2, 0, barW, imgH);

          const r = Math.max(14, Math.min(imgW, imgH) * 0.03);
          const cy = imgH / 2;

          ctx.beginPath();
          ctx.arc(wipeX, cy, r, 0, Math.PI * 2);
          ctx.fillStyle = "rgba(255,255,255,0.95)";
          ctx.fill();

          ctx.strokeStyle = "rgba(0,0,0,0.55)";
          ctx.lineWidth = Math.max(2, r * 0.12);
          ctx.lineCap = "round";
          ctx.lineJoin = "round";

          // left arrow
          ctx.beginPath();
          ctx.moveTo(wipeX - r * 0.15, cy);
          ctx.lineTo(wipeX - r * 0.45, cy - r * 0.20);
          ctx.moveTo(wipeX - r * 0.15, cy);
          ctx.lineTo(wipeX - r * 0.45, cy + r * 0.20);
          ctx.stroke();

          // right arrow
          ctx.beginPath();
          ctx.moveTo(wipeX + r * 0.15, cy);
          ctx.lineTo(wipeX + r * 0.45, cy - r * 0.20);
          ctx.moveTo(wipeX + r * 0.15, cy);
          ctx.lineTo(wipeX + r * 0.45, cy + r * 0.20);
          ctx.stroke();

          ctx.restore();
        };

        // choose overlay (mask first, else depth)
        const overlayB64 = props?.each_image_detail?.depth_map_url;

        const overlayRef =
          props?.each_image_detail?.mask_url ? maskImgRef : depthImgRef;

        if (overlayB64) {
          if (!overlayRef.current) {
            const overlayImg = new Image();
            overlayImg.src = `data:image/png;base64,${overlayB64}`;
            overlayImg.onload = () => {
              overlayRef.current = overlayImg;
              drawOverlayWipe(overlayImg);
            };
          } else {
            drawOverlayWipe(overlayRef.current);
          }
        }
        // -------------------------------------------------------------------

        // draw mask if present
        // if (props?.pridictions && props?.each_image_detail?.mask_url) {
        //   if (!maskImgRef.current) {
        //     const maskImg = new Image();
        //     maskImg.src = `data:image/png;base64,${props.each_image_detail.mask_url}`;
        //     maskImg.onload = () => {
        //       maskImgRef.current = maskImg;
        //       ctx.save();
        //       ctx.globalAlpha = Number.isFinite(props.opacity) ? props.opacity : 1;
        //       ctx.drawImage(maskImg, 0, 0, img.width, img.height);
        //       ctx.restore();
        //     };
        //   } else {
        //     ctx.save();
        //     ctx.globalAlpha = Number.isFinite(props.opacity) ? props.opacity : 1;
        //     ctx.drawImage(maskImgRef.current, 0, 0, img.width, img.height);
        //     ctx.restore();
        //   }
        // }
        if (props?.pridictions && props?.each_image_detail?.mask_url) {
          await drawSegmentationMasks(ctx, img);
          if (cancelled) return;
        }
        // if (props?.groundtruth && props?.each_image_detail?.task_type === "segmentation" && props?.each_image_detail?.ground_truth?.[0]) {
        //   if (!maskImgRef.current) {
        //     const maskImg = new Image();
        //     maskImg.src = `data:image/png;base64,${props?.each_image_detail?.ground_truth?.[0]}`;
        //     maskImg.onload = () => {
        //       maskImgRef.current = maskImg;
        //       ctx.save();
        //       ctx.globalAlpha = Number.isFinite(props.opacity) ? props.opacity : 1;
        //       ctx.drawImage(maskImg, 0, 0, img.width, img.height);
        //       ctx.restore();
        //     };
        //   } else {
        //     ctx.save();
        //     ctx.globalAlpha = Number.isFinite(props.opacity) ? props.opacity : 1;
        //     ctx.drawImage(maskImgRef.current, 0, 0, img.width, img.height);
        //     ctx.restore();
        //   }
        // }

        if (
          props?.groundtruth &&
          props?.each_image_detail?.task_type === "segmentation" &&
          props?.each_image_detail?.ground_truth?.[0]
        ) {
          const gtMask = props?.each_image_detail?.ground_truth?.[0];

          let gtImage = maskImgRef.current.get("__gt__");
          if (!gtImage) {
            gtImage = await loadImage(gtMask);
            if (cancelled) return;
            if (gtImage) {
              maskImgRef.current.set("__gt__", gtImage);
            }
          }

          if (gtImage) {
            ctx.save();
            ctx.globalAlpha = getOverlayOpacity(props.opacity, 1);
            ctx.drawImage(gtImage, 0, 0, img.width, img.height);
            ctx.restore();
          }
        }
        // if (props?.each_image_detail?.depth_map_url) {
        //   if (!maskImgRef.current) {
        //     const maskImg = new Image();
        //     maskImg.src = `data:image/png;base64,${props.each_image_detail.depth_map_url}`;
        //     maskImg.onload = () => {
        //       maskImgRef.current = maskImg;
        //       ctx.save();
        //       ctx.globalAlpha = Number.isFinite(props.opacity) ? props.opacity : 0.4;
        //       ctx.drawImage(maskImg, 0, 0, img.width, img.height);
        //       ctx.restore();
        //     };
        //   } else {
        //     ctx.save();
        //     ctx.globalAlpha = Number.isFinite(props.opacity) ? props.opacity : 0.4;
        //     ctx.drawImage(maskImgRef.current, 0, 0, img.width, img.height);
        //     ctx.restore();
        //   }
        // }

        if (props.plottype === "pose") {
          try {
            const detail = props.modelDetails ?? props.each_image_detail ?? {};

            const labelNames = Array.isArray(detail?.label?.[0]) ? detail.label[0] : [];
            const groundTruthLabelNames = Array.isArray(detail?.gt_labels)
              ? detail.gt_labels.reduce((labels, item, idx) => {
                const labelData = Array.isArray(item) ? item[0] : item;
                const pointNumber = Number(
                  Array.isArray(item)
                    ? item[1]
                    : labelData?.number ?? labelData?.point_number ?? idx + 1
                );
                const labelName = typeof labelData === "string"
                  ? labelData
                  : labelData?.name ?? labelData?.label ?? labelData?.class_name ?? labelData?.class;

                if (labelName) {
                  labels[Number.isFinite(pointNumber) ? pointNumber - 1 : idx] = labelName;
                }
                return labels;
              }, [])
              : labelNames;
          
            const predictionSkeleton = Array.isArray(detail?.reference_skeleton)
              ? detail.reference_skeleton
              : [];

            const groundTruthSkeleton = Array.isArray(detail?.gt_skeleton)
              ? detail.gt_skeleton
              : [];

            const scores = Array.isArray(detail?.score) ? detail.score : [];
            const threshold = Number(props?.scoreThreshold ?? 0);

            let predRaw = detail?.skeleton;

            if (Array.isArray(predRaw) && predRaw.length === 1 && Array.isArray(predRaw[0])) {
              predRaw = predRaw[0];
            }

            const gtRaw =
              detail?.ground_truth ??
              props?.each_image_detail?.ground_truth ??
              props?.ground_truth ??
              props?.Groundtruth;

            const isPoint = (p) =>
              Array.isArray(p) &&
              p.length >= 2 &&
              Number.isFinite(p[0]) &&
              Number.isFinite(p[1]);

            const isEdge = (e) =>
              Array.isArray(e) &&
              e.length === 2 &&
              isPoint(e[0]) &&
              isPoint(e[1]);

            const isKeypointArray = (arr) =>
              Array.isArray(arr) &&
              arr.length > 0 &&
              isPoint(arr[0]) &&
              !isEdge(arr);

            const isEdgeArray = (arr) =>
              Array.isArray(arr) &&
              arr.length > 0 &&
              isEdge(arr[0]);

            const normalizePersons = (raw) => {
              if (!Array.isArray(raw) || raw.length === 0) return [];

              if (isKeypointArray(raw) || isEdgeArray(raw)) return [raw];

              if (Array.isArray(raw[0]) && (isKeypointArray(raw[0]) || isEdgeArray(raw[0]))) {
                return raw;
              }

              if (
                Array.isArray(raw[0]) &&
                Array.isArray(raw[0][0]) &&
                (isKeypointArray(raw[0][0]) || isEdgeArray(raw[0][0]))
              ) {
                return raw.flat();
              }

              return [];
            };

            const FIXED_KP_COLOR = props?.keypointColor ?? "#49ca90";

            const toPositiveNumber = (value) => {
              const number = Number(value);
              return Number.isFinite(number) && number > 0 ? number : null;
            };

            const sourceWidth =
              toPositiveNumber(detail?.img_width) ??
              toPositiveNumber(detail?.image_width) ??
              toPositiveNumber(detail?.width) ??
              toPositiveNumber(detail?.natural_width) ??
              toPositiveNumber(detail?.image_properties?.width) ??
              toPositiveNumber(detail?.imageProperties?.width) ??
              toPositiveNumber(detail?.metadata?.width);

            const sourceHeight =
              toPositiveNumber(detail?.img_height) ??
              toPositiveNumber(detail?.image_height) ??
              toPositiveNumber(detail?.height) ??
              toPositiveNumber(detail?.natural_height) ??
              toPositiveNumber(detail?.image_properties?.height) ??
              toPositiveNumber(detail?.imageProperties?.height) ??
              toPositiveNumber(detail?.metadata?.height);

            const coordinateScaleX = sourceWidth ? img.width / sourceWidth : 1;
            const coordinateScaleY = sourceHeight ? img.height / sourceHeight : 1;

            const canvasRect = ctx.canvas.getBoundingClientRect?.();
            const displayScaleX = canvasRect?.width ? logicalCanvasWidth / canvasRect.width : 1;
            const displayScaleY = canvasRect?.height ? logicalCanvasHeight / canvasRect.height : 1;
            const displayScale = Math.max(1, displayScaleX, displayScaleY);

            const toCanvasPoint = (point) => {
              if (!isPoint(point)) return point;

              const x = Number(point[0]);
              const y = Number(point[1]);
              const isNormalized = Math.abs(x) <= 1 && Math.abs(y) <= 1;

              return [
                isNormalized ? x * img.width : x * coordinateScaleX,
                isNormalized ? y * img.height : y * coordinateScaleY,
                ...point.slice(2),
              ];
            };

            const getPointIndexFromLabelNumber = (point) => {
              const pointNumber = Number(point?.[2]);
              return Number.isFinite(pointNumber) ? pointNumber - 1 : null;
            };

            const getPointColor = (idx, point) => {
              const colorIdx = getPointIndexFromLabelNumber(point) ?? idx;
              const labelName = labelNames[colorIdx];
              return props?.currentColors?.[labelName] ?? FIXED_KP_COLOR;
            };

            const normalizeLabelKey = (value) =>
              String(value ?? "")
                .trim()
                .toLowerCase()
                .replace(/[\s_]+/g, "");

            const isPointVisibleByLabel = (idx, point, names = labelNames) => {
              if (props?.allLabelsHidden) return false;
              if (!props?.selectedLabelKey) return true;

              const pointIdx = getPointIndexFromLabelNumber(point) ?? idx;
              return normalizeLabelKey(names?.[pointIdx]) === props.selectedLabelKey;
            };

            const DOT_RADIUS_CSS = 4;
            const LINE_WIDTH_CSS = 3;
            const LABEL_FONT_CSS = 14;

            const dotRadius = DOT_RADIUS_CSS * displayScale;
            const lineWidth = LINE_WIDTH_CSS * displayScale;
            const labelFont = LABEL_FONT_CSS * displayScale;

            const showLines = props?.pridictions;
            const showgtLines = props?.groundtruth;

            const isPointVisibleByScore = (idx) => {
              const rawScore = scores?.[idx];
              const roundedScore = Number(Number(rawScore ?? 0).toFixed(2));
              return roundedScore >= threshold;
            };

            const getGroundTruthSkeleton = (kpOrEdges) => {
              if (!isEdgeArray(kpOrEdges)) return groundTruthSkeleton;

              return kpOrEdges.map(([p1, p2], idx) => {
                const [fallbackStartIdx, fallbackEndIdx] = groundTruthSkeleton?.[idx] || [];
                const startIdx = getPointIndexFromLabelNumber(p1) ?? fallbackStartIdx;
                const endIdx = getPointIndexFromLabelNumber(p2) ?? fallbackEndIdx;

                return [startIdx, endIdx];
              });
            };

            const toKeypoints = (kpOrEdges, skeletonRef = predictionSkeleton) => {
              if (isKeypointArray(kpOrEdges)) return kpOrEdges.map(toCanvasPoint);

              if (isEdgeArray(kpOrEdges) && Array.isArray(skeletonRef) && skeletonRef.length) {
                const kp = [];
                const n = Math.min(skeletonRef.length, kpOrEdges.length);

                for (let i = 0; i < n; i++) {
                  const [sIdx, eIdx] = skeletonRef[i] || [];
                  const [p1, p2] = kpOrEdges[i] || [];

                  if (Number.isFinite(sIdx) && sIdx >= 0 && isPoint(p1)) kp[sIdx] = toCanvasPoint(p1);
                  if (Number.isFinite(eIdx) && eIdx >= 0 && isPoint(p2)) kp[eIdx] = toCanvasPoint(p2);
                }
                return kp;
              }

              return [];
            };

            const drawOnePerson = (kpOrEdges, opts = {}) => {
              const {
                pointColorFn,
                pointColor,
                lineColor,
                forceDrawLines,
                drawPoints = true,
                drawLabels = false,
                alphaOverride,
                applyScoreThreshold = false,
                lineDash = [],
                skeletonRef = predictionSkeleton,
                pointLabelNames = labelNames,
              } = opts;

              const keypoints = toKeypoints(kpOrEdges, skeletonRef);

              // ---------- POINTS ----------
              if (drawPoints) {
                keypoints.forEach((pt, idx) => {
                  if (!isPoint(pt)) return;
                  if (pt.length >= 3 && !pt[2]) return;
                  if (!isPointVisibleByLabel(idx, pt, pointLabelNames)) return;

                  if (applyScoreThreshold && !isPointVisibleByScore(idx)) return;

                  const [x, y] = pt;
                  const c = pointColorFn ? pointColorFn(idx) : (pointColor ?? getPointColor(idx, pt));

                  ctx.save();
                  ctx.fillStyle = c;
                  ctx.globalAlpha = Number.isFinite(alphaOverride) ? alphaOverride : 1;

                  ctx.beginPath();
                  ctx.arc(x, y, dotRadius, 0, 2 * Math.PI);
                  ctx.fill();

                  // if (drawLabels && props?.labelvisible) {
                  //   const pointLabel = Number.isFinite(Number(pt?.[2])) ? pt[2] : idx + 1;
                    
                  //   ctx.fillStyle = c;
                  //   ctx.font = `${labelFont}px sans-serif`;
                  //   ctx.fillText(pointLabel, x + dotRadius * 0.6, y - dotRadius * 0.6);
                  // }
                  if (drawLabels && props?.labelvisible) {
                    const pointLabel = Number.isFinite(Number(pt?.[2])) ? pt[2] : idx + 1;
                    const labelX = x + dotRadius * 0.8;
                    const labelY = y - dotRadius * 0.8;

                    ctx.font = `bold ${labelFont}px sans-serif`;
                    ctx.lineWidth = 5;
                    ctx.strokeStyle = "transparent"; // outline for better readability
                    ctx.fillStyle = c;
                    ctx.strokeText(pointLabel, labelX, labelY);
                    ctx.fillText(pointLabel, labelX, labelY);
                  }
                  ctx.restore();
                });
              }

              // ---------- LINES ----------
              const shouldDrawLines = forceDrawLines === undefined ? showLines : forceDrawLines;
              if (!shouldDrawLines) return;

              if (Array.isArray(skeletonRef) && skeletonRef.length) {
                skeletonRef.forEach(([startIdx, endIdx]) => {
                  const p1 = keypoints?.[startIdx];
                  const p2 = keypoints?.[endIdx];

                  if (!isPoint(p1) || !isPoint(p2)) return;
                  if (
                    !isPointVisibleByLabel(startIdx, p1, pointLabelNames) ||
                    !isPointVisibleByLabel(endIdx, p2, pointLabelNames)
                  ) {
                    return;
                  }

                  if (
                    applyScoreThreshold &&
                    (!isPointVisibleByScore(startIdx) || !isPointVisibleByScore(endIdx))
                  ) {
                    return;
                  }

                  ctx.save();
                  ctx.strokeStyle = lineColor ?? "#1677ff";
                  ctx.lineWidth = lineWidth;
                  ctx.setLineDash(lineDash.map((dash) => dash * displayScale));
                  ctx.globalAlpha = Number.isFinite(alphaOverride)
                    ? alphaOverride
                    : (Number.isFinite(props.opacity) ? props.opacity : 0.4);

                  ctx.beginPath();
                  ctx.moveTo(p1[0], p1[1]);
                  ctx.lineTo(p2[0], p2[1]);
                  ctx.stroke();
                  ctx.restore();
                });
              }
            };

            const gtPersons = normalizePersons(gtRaw);
            const predPersons = normalizePersons(predRaw);

            // Ground truth - no score filtering
            gtPersons.forEach((person) => {
              const skeletonRef = getGroundTruthSkeleton(person);

              drawOnePerson(person, {
                lineColor: "#1677ff",
                drawPoints: showgtLines,
                drawLabels: showgtLines,
                forceDrawLines: showgtLines,
                applyScoreThreshold: false,
                lineDash: [],
                skeletonRef,
                pointLabelNames: groundTruthLabelNames,
              });
            });

            // Predictions - apply score threshold
            predPersons.forEach((person) => {
              drawOnePerson(person, {
                lineColor: "#1677ff",
                drawPoints: showLines,
                drawLabels: showLines,
                forceDrawLines: showLines,
                applyScoreThreshold: true,
                lineDash: [10, 10],
                skeletonRef: predictionSkeleton,
                pointLabelNames: labelNames,
              });
            });
          } catch (e) {
            console.warn("Keypoints draw error:", e);
          }
        }


        // POLYGONS or BBOXES from GROUNDTRUTH
        if (props.Groundtruth && props.plottype !== "pose") {
          let gt = props.Groundtruth;

          const wantPolygons =
            props.plottype === "polygon" ||
            looksLikePolygon(gt) ||
            isPolygonRow(gt);

          if (wantPolygons) {
            // polygons (flat or nested payload shape)
            const polyArr = normalizePolygonRows(gt);
            (polyArr || []).forEach((x, i) => {
              const length = x?.length - 2;
              const firstHalf = x?.slice(0, length + 1);
              const secondHalf = x?.slice(-1)[0];
              const label = secondHalf?.[0];
              const labelColor = secondHalf?.[1];

              try {
                drawPolygonWithFill(
                  props?.labelvisible ? label : " ",
                  firstHalf,
                  labelColor,
                  0,
                  0,
                  getHoverAwareOpacity(props.opacity, {
                    source: "groundtruth",
                    index: i,
                    label,
                  }),
                  props.currentColors?.[label] ?? props.currentColors?.[labelColor],
                  [],
                );
              } catch (err) {
                console.log("Polygon draw error:", err);
              }
            });
          } else {
            // bboxes (new flat OR old nested)
            const boxes = isBBoxArray(gt)
              ? gt
              : isOldNestedBBoxArray(gt)
                ? gt[0]
                : Array.isArray(gt?.[0])
                  ? gt
                  : [];

            boxes.forEach((x, i) => {
              if (!Array.isArray(x)) return;
              const [bx, by, bw, bh, label, color] = x;
              const maybeTrackId = x.length > 6 ? x[x.length - 1] : undefined;

              drawDashedLine(
                bx,
                by,
                bw,
                bh,
                props?.labelvisible ? label : " ",
                color,
                [], // pattern
                1, // line (unused)
                "#fff",
                0.5, // number (bugfix)
                getHoverAwareOpacity(props.opacity, {
                  source: "groundtruth",
                  index: i,
                  label,
                }),
                maybeTrackId,
                props?.currentColors?.[label]
              );
            });
          }
        }

        // ANNOTATIONS (predictions)
        if (props.annotations && props.plottype !== "pose") {
          let annotation =
            props.onEpochChange
              ? props.annotations?.[0]
              : props.epochLength > 1 && props.type !== "dataset_versions"
                ? props.annotations[Math.floor((currentEpoch ?? props.epochLength) / (props.epochspacing || 1))]
                : props.annotations[0];

          if (props.plottype === "polygon" && props.type === "dataset_versions") {
            annotation = props.annotations[0];
          }

          const pattern = [10, 10];

          (annotation || []).forEach((x, i) => {
            if (props.plottype === "polygon") {
              const length = x?.length - 1;
              const firstHalf = x?.slice(0, length);
              const secondHalf = x?.slice(-1)[0];
              const label = props?.labelvisible ? secondHalf?.[0] : " ";
              const labelColor = secondHalf?.[1];

              try {
                drawPolygonWithFill(
                  label,
                  firstHalf,
                  labelColor,
                  0,
                  0,
                  getHoverAwareOpacity(props.opacity, {
                    source: "prediction",
                    index: i,
                    label: secondHalf?.[0],
                  }),
                  props.currentColors?.[label] ?? props.currentColors?.[labelColor],
                  pattern
                );
              } catch (err) {
                console.log("Polygon annotation draw error:", err);
              }
            } else {
              // bbox annotations: x = [x,y,w,h,label,color,track_id?]
              drawDashedLine(
                x[0],
                x[1],
                x[2],
                x[3],
                props?.labelvisible ? x?.[4] : " ",
                x[5],
                pattern,
                4,
                x[5],
                0.5,
                getHoverAwareOpacity(props.opacity, {
                  source: "prediction",
                  index: i,
                  label: x?.[4],
                }),
                x.length > 6 ? x[x.length - 1] : undefined,
                props?.currentColors?.[x?.[4]],
                props?.landmarks?.[0]?.[i]
              );
            }
          });
        }

        drawSuperpointLandmarks();

        commitCanvas(ctx.canvas);

        if (!cancelled) {
          props?.onImageReady?.();
        }
      };
      img.onerror = () => {
        if (!cancelled) {
          props?.onImageReady?.();
        }
      };
      loadRenderableImageSource(props.src)
        .then((source) => {
          if (!cancelled) img.src = source;
        })
        .catch((error) => {
          console.error("Unable to render DICOM image", error);
          if (!cancelled) props?.onImageReady?.();
        });
    } else {
      // Fallback when no image
      ctx.canvas.width = 1000;
      ctx.canvas.height = 1000;
      ctx.textAlign = "center";
      ctx.font = "bold 40px Montserrat";
      ctx.fillText("No Data found please clear filter", 500, 500);
    }

    return () => {
      cancelled = true;
      if (activeImage) {
        activeImage.onload = null;
        activeImage.onerror = null;
      }
    };
  }, [
    currentEpoch,
    reveal,
    props.Groundtruth,
    props.annotations,
    props.Image,
    props.opacity,
    props.currentColors,
    props.imageName,
    props.src,
    props.epochLength,
    props.epochspacing,
    props.plottype,
    props.pridictions,
    props.groundtruth,
    props.type,
    props.each_image_detail,
    props.labelvisible,
    props.landmarks,
    props.modelName,
    props.scoreThreshold,
    props.taskTypes,
    props.hoveredAnnotation,
    props.selectedLabelKey,
    props.allLabelsHidden,
    transform?.scale,
  ]);

  // useEffect(() => {
  //   maskImgRef.current = null;
  // }, [props?.each_image_detail?.mask_url]);

  useEffect(() => {
    maskImgRef.current = new Map();
  }, [props?.each_image_detail?.mask_url, props?.imageName]);

  useEffect(() => {
    depthImgRef.current = null;
  }, [props?.each_image_detail?.depth_map_url]);


  const isPair = props.src && typeof props.src === "object";
  const singleSrc = !isPair ? props.src : undefined;
  const src1 = isPair ? props.src.src1 : undefined;
  const src2 = isPair ? props.src.src2 : undefined;

  const canvas = canvasRef.current;
  const wrapper = wrapperRef.current;

  let sliderLeft = 0;

  if (canvas && wrapper) {
    const canvasRect = canvas.getBoundingClientRect();
    const wrapperRect = wrapper.getBoundingClientRect();

    sliderLeft =
      canvasRect.left -
      wrapperRect.left +
      canvasRect.width * reveal;
  }


  const isDepthEstimation =
    props?.each_image_detail?.task_type === "depth_estimation"

  const canvasStyle = {
    width: imageSize.width > 0 ? `${imageSize.width}px` : "100%",
    height: imageSize.height > 0 ? `${imageSize.height}px` : "auto",
    maxWidth: "100%",
    maxHeight: "calc(100vh - 390px)",
    objectFit: "contain",
    marginRight: 0,
    flex: "0 1 auto",
    display: "block",
  };

  return (
    <div
      className="canvas-container w-100"
      style={{ position: "relative", height: "100%", minHeight: 0 }}
    >
      {props.epochLength > 1 && props.type !== "dataset_versions" && !props.hideEpochSlider && (
        <div
          className="d-flex grey-card align-items-center justify-content-between px-3 py-2 epoch"
          style={{
            position: "absolute",
            bottom: 8,
            right: 8,
            zIndex: 9999,
            overflow: "hidden",
            width: 200,
          }}
        >
          <div className=" mx-3 my-2">
            <label
              htmlFor="customRange"
              className="form-label"
              style={{ fontSize: 10, position: "absolute", top: -7 }}
            >
              Epoch {currentEpoch ?? props.epochLength} of {props.epochLength}
            </label>
            <Input
              type="range"
              className="form-range mt-2 pt-1"
              min="0"
              max={props.epochLength}
              step={props.epochspacing ? props.epochspacing : 1}
              id="customRange"
              value={props.controlledEpoch ?? currentEpoch ?? props.epochLength ?? 0}
              onChange={(e) => {
                const nextEpoch = Number(e.target.value);
                if (props.controlledEpoch === undefined) {
                  setCurrentEpoch(nextEpoch);
                }
                props.onEpochChange?.(nextEpoch);
              }}
            />
          </div>
        </div>
      )}

      {/* {(props.plottype === "classification" || props.plottype === "label") && (
        <div className="classification-card p-2">
          {(props.Groundtruth || []).map((item, idx) => (
            <div
              key={`gt-${idx}`}
              className="d-flex align-items-center grey-card p-2 ms-2 pt-4"
              style={{ minWidth: 120, position: "relative" }}
            >
              <div
                style={{
                  position: "absolute",
                  fontSize: 12,
                  top: 4,
                  height: 10,
                  left: 10,
                }}
              >
                Ground truth
              </div>
              <div
                style={{
                  background: item?.[5],
                  padding: 7,
                  width: 5,
                  display: "inline-block",
                  borderRadius: 5,
                }}
              />
              <div className="h4 ms-2"> {item?.[4]}</div>
            </div>
          ))}

          {!!(props.annotations?.length) && !props?.time && (
            <div
              className="d-flex align-items-center grey-card p-2 pt-4"
              style={{ minWidth: 120, position: "relative" }}
            >
              <div
                style={{
                  position: "absolute",
                  fontSize: 12,
                  top: 4,
                  height: 10,
                  left: 10,
                }}
              >
                prediction
              </div>
              <div
                style={{
                  background:
                    (currentEpoch ?? 0) > 0
                      ? props.annotations?.[currentEpoch]?.[0]?.[5]
                      : props.annotations?.[0]?.[5],
                  padding: 7,
                  width: 5,
                  display: "inline-block",
                  borderRadius: 5,
                }}
              />
              <div className="h4 ms-2">
                {(currentEpoch ?? 0) > 0
                  ? props.annotations?.[currentEpoch]?.[0]?.[4]
                  : props.annotations?.[0]?.[4]}
              </div>
            </div>
          )}
        </div>
      )} */}

      <span className="canvas-mask"></span>

      {props?.showProperties === false && (
        <div
          className="d-flex grey-card align-items-center justify-content-between px-3 py-1 my-1"
          style={{
            position: "absolute",
            top: -10,
            zIndex: 9999,
            overflow: "hidden",
            width: "99%",
          }}
        >
          <h6 className="mb-0 px-3 border-right d-flex align-items-center">
            <p
              className="notification-message mb-0"
              style={{ display: "inline-block", whiteSpace: "nowrap" }}
              title={props?.imageName}
            >
              {props?.imageName}
            </p>
          </h6>

        </div>
      )}

      <Draggable
        nodeRef={zoomControlsRef}
        bounds="parent"
        cancel="button"
        position={props.zoomControlsPosition ?? { x: 0, y: 0 }}
        onStop={(_event, data) =>
          props.onZoomControlsPositionChange?.({ x: data.x, y: data.y })
        }
      >
        <div
          ref={zoomControlsRef}
          className="image-zoom-controls"
          role="toolbar"
          aria-label="Image zoom controls"
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => event.stopPropagation()}
        >
          <StyledTooltip title="Zoom in" placement="right">
            <button type="button" onClick={zoomIn} aria-label="Zoom in">
              <img draggable="false" alt="" src="/ico/zoom-in.png" />
            </button>
          </StyledTooltip>
          <StyledTooltip title="Zoom out" placement="right">
            <button type="button" onClick={zoomOut} aria-label="Zoom out">
              <img draggable="false" alt="" src="/ico/zoom-out.png" />
            </button>
          </StyledTooltip>
          <StyledTooltip title="Reset zoom" placement="right">
            <button type="button" onClick={resetZoom} aria-label="Reset zoom">
              <img draggable="false" alt="" src="/ico/zoom-fit.png" />
            </button>
          </StyledTooltip>
        </div>
      </Draggable>

      {/* {props.dicomSliceControl?.enabled && props.dicomSliceControl.total > 1 && (
        <Draggable
          nodeRef={dicomSliderRef}
          bounds="parent"
          cancel="button,input"
          position={props.dicomSliderPosition ?? { x: 0, y: 0 }}
          onStop={(_event, data) =>
            props.onDicomSliderPositionChange?.({ x: data.x, y: data.y })
          }
        >
          <div
            ref={dicomSliderRef}
            className="dicom-slice-control"
            role="group"
            aria-label="DICOM slice navigation"
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
          >
            <span className="dicom-slice-control__title">SLICE</span>
            <button
              type="button"
              disabled={props.dicomSliceControl.loading || dicomDraftSlice <= 1}
              onClick={() => {
                const nextValue = Math.max(1, dicomDraftSlice - 1);
                setDicomDraftSlice(nextValue);
              }}
              aria-label="Previous DICOM slice"
            >
              ▲
            </button>
            <input
              className="dicom-slice-control__range"
              type="range"
              min="1"
              max={props.dicomSliceControl.total}
              step="1"
              value={dicomDraftSlice}
              disabled={props.dicomSliceControl.loading}
              onChange={(event) => setDicomDraftSlice(Number(event.target.value))}
              aria-label={`DICOM slice ${dicomDraftSlice} of ${props.dicomSliceControl.total}`}
            />
            <button
              type="button"
              disabled={
                props.dicomSliceControl.loading ||
                dicomDraftSlice >= props.dicomSliceControl.total
              }
              onClick={() => {
                const nextValue = Math.min(
                  props.dicomSliceControl.total,
                  dicomDraftSlice + 1
                );
                setDicomDraftSlice(nextValue);
              }}
              aria-label="Next DICOM slice"
            >
              ▼
            </button>
            <span className="dicom-slice-control__count">
              {dicomDraftSlice}/{props.dicomSliceControl.total}
            </span>
            {props.dicomSliceControl.loading && (
              <span className="dicom-slice-control__loading" aria-label="Loading DICOM slice" />
            )}
          </div>
        </Draggable>
      )} */}

      <div
        ref={wrapperRef}
        className="image-zoom-viewport"
        {...viewportHandlers}
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {/* Gesture layer sits above the image and captures depth-wipe drags. */}
        {props?.each_image_detail?.depth_map_url && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 5,
              cursor: draggingWipe ? "col-resize" : "default",
            }}
            onPointerDown={(e) => {
              const canvas = canvasRef.current;
              if (!canvas) return;

              const rect = canvas.getBoundingClientRect();
              const cssX = e.clientX - rect.left;
              const revealPx = rect.width * reveal;

              const HIT_SLOP = 20; // 👈 adjustable grab distance

              if (Math.abs(cssX - revealPx) < HIT_SLOP) {
                e.preventDefault();
                e.stopPropagation();
                setDraggingWipe(true);
                e.currentTarget.setPointerCapture(e.pointerId);
              }
            }}
            onPointerMove={(e) => {
              if (!draggingWipe) return;

              const canvas = canvasRef.current;
              if (!canvas) return;

              const rect = canvas.getBoundingClientRect();
              const cssX = e.clientX - rect.left;
              const reveal01 = Math.max(0, Math.min(1, cssX / rect.width));

              setReveal(reveal01);
            }}
            onPointerUp={(e) => {
              if (!draggingWipe) return;
              setDraggingWipe(false);
              e.currentTarget.releasePointerCapture(e.pointerId);
            }}
          />
        )}
        <div
          className={isDepthEstimation ? "d-flex justify-content-center" : ""}
          style={{
            width: "fit-content",
            height: "fit-content",
            maxWidth: "100%",
            maxHeight: "100%",
            flex: "0 0 auto",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1,
            ...transformStyle,
          }}
        >
          <canvas ref={canvasRef} className="canvas-img" style={canvasStyle} />
        </div>
      </div>
    </div>
  );
}
